import { cn } from '@/shared/lib/utils'

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const MONTHS_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
const MONTHS_SHORT = MONTHS_FULL.map((month) => month.slice(0, 3))

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function isBeforeDay(date: Date, boundary: Date): boolean {
  return (
    date.getFullYear() < boundary.getFullYear() ||
    (date.getFullYear() === boundary.getFullYear() && date.getMonth() < boundary.getMonth()) ||
    (date.getFullYear() === boundary.getFullYear() &&
      date.getMonth() === boundary.getMonth() &&
      date.getDate() < boundary.getDate())
  )
}

function isAfterDay(date: Date, boundary: Date): boolean {
  return (
    date.getFullYear() > boundary.getFullYear() ||
    (date.getFullYear() === boundary.getFullYear() && date.getMonth() > boundary.getMonth()) ||
    (date.getFullYear() === boundary.getFullYear() &&
      date.getMonth() === boundary.getMonth() &&
      date.getDate() > boundary.getDate())
  )
}

function formatAriaLabel(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export interface CalendarGridProps {
  viewYear: number
  viewMonth: number
  selected: Date | undefined
  today: Date
  minDate?: Date
  maxDate?: Date
  onSelect: (date: Date) => void
}

export function CalendarGrid({
  viewYear,
  viewMonth,
  selected,
  today,
  minDate,
  maxDate,
  onSelect,
}: CalendarGridProps) {
  const firstDay = new Date(viewYear, viewMonth, 1)
  const startOffset = firstDay.getDay()
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
  const daysInPrev = new Date(viewYear, viewMonth, 0).getDate()

  const cells: { date: Date; current: boolean }[] = []

  for (let i = startOffset - 1; i >= 0; i--) {
    cells.push({ date: new Date(viewYear, viewMonth - 1, daysInPrev - i), current: false })
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(viewYear, viewMonth, d), current: true })
  }
  // REBUILD-430: always render a fixed 6-row (42-cell) grid. A month spans 4, 5, or 6 week
  // rows depending on its length and start weekday; letting the grid grow/shrink resizes the
  // popover, which then reflows/repositions on month navigation and makes the box "chase" the
  // user's cursor. Padding to a constant 42 cells keeps the rendered height identical across
  // every month, so the popover stays the same size and position (fixed at the shared component
  // so every date picker inherits it).
  const TOTAL_CELLS = 42
  while (cells.length < TOTAL_CELLS) {
    cells.push({
      date: new Date(viewYear, viewMonth + 1, cells.length - daysInMonth - startOffset + 1),
      current: false,
    })
  }

  const weeks: (typeof cells)[] = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))

  return (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr>
          {WEEKDAYS.map((d) => (
            <th key={d} className="pb-1 text-center text-xs font-medium text-muted-foreground w-8">
              {d}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {weeks.map((week, wi) => (
          <tr key={wi}>
            {week.map(({ date, current }, di) => {
              const isSelected = selected ? isSameDay(date, selected) : false
              const isToday = isSameDay(date, today)
              const isDisabled =
                (minDate && isBeforeDay(date, minDate)) || (maxDate && isAfterDay(date, maxDate))
              return (
                <td key={di} className="p-0.5 text-center">
                  <button
                    type="button"
                    onClick={() => onSelect(date)}
                    disabled={isDisabled}
                    aria-label={formatAriaLabel(date)}
                    className={cn(
                      'h-8 w-8 rounded-md text-sm transition-colors',
                      !current && 'text-muted-foreground/50',
                      isDisabled && 'opacity-30 cursor-not-allowed',
                      current && !isSelected && !isToday && !isDisabled && 'hover:bg-accent',
                      isToday && !isSelected && 'border border-primary text-primary',
                      isSelected && 'bg-primary text-primary-foreground hover:bg-primary/90',
                    )}
                  >
                    {date.getDate()}
                  </button>
                </td>
              )
            })}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export { MONTHS_FULL, MONTHS_SHORT, isSameDay, isBeforeDay, isAfterDay }
